<template>
  <div
    v-if="sources.length"
    class="image"
    :class="[
      { 'image--background' : !inlineImage },
      { 'is-loading' : state === 'loading' },
    ]"
  >
    <span
      ref="spacer"
      class="image__spacing"
      aria-hidden="true"
    />

    <picture class="image__picture">
      <source
        v-for="(source) in sources"
        :key="`source-${source.breakpoint}`"
        class="image__img"
        :media="source.media"
        :srcset="source.src"
        :data-srcset="source.dataSrc"
      >

      <img
        v-if="image.src || imageDesktop.src"
        class="image__img"
        :class="{ 'lazyload': lazyload }"
        :alt="imageAlt"
        decoding="async"
        :loading="lazyload ? 'lazy' : 'eager'"
        :role="role"
        :src="getFallbackImageSrc(false)"
        :data-src="getFallbackImageSrc(true)"
      >
    </picture>
  </div>
</template>

<script>

/**
 * Component: Image Element
 * ------------------------------------------------------------------------------
 * - Core component for an Image Element component
 * - Use this component for 'Art Direction' images - showing different
 *   images at different viewports (i.e. desktop and mobile)
 * - Will scale the image depending on the viewport, but not as fine-tuned
 *   as the ResponsiveImage component.
 * - Styles are loaded in compiled SCSS.
 * - Can be used for inline images
 * - Can be used for background images / hero banners
 *
 * @param {String} [alt] - Override default alt text
 * @param {Boolean} [inlineImage] - Display the image as an inline
 * element instead of background
 * @param {Object} [imageDesktop] - A formatted image object - specific to desktop. (>= 1024px)
 * @param {Object} image - A formatted image object.
 * @param {Boolean} lazyload - Lazyload the image - defaulting to `true`
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { values as breakpoints } from '~/helpers/breakpoints'
import { getSizedImageUrl } from '~/helpers/format'

export default {
  name: 'ImageElement',

  props: {
    alt: {
      type: String,
      default: '',
    },
    inlineImage: {
      type: Boolean,
      default: false,
    },
    image: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    imageDesktop: {
      type: [Boolean, Object],
      default: false,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['set-aspect-ratio'],

  data() {
    return {
      aspectRatio: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the image alt.
     * - Attempts to retrieve from the image object but displays
     *   the override prop if present.
     * @returns {String}
     */
    imageAlt() {
      let image = this.image

      if (this.imageDesktop && !this.isMobile) {
        image = this.imageDesktop
      }

      return this.alt || image.alt
    },

    /**
     * Calculate an accessible, SEO friendly role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },

    /**
     * Calculate an array of image sources based on the breakpoints
     * - Respects desktop-specific image URL
     * - Loads sized image from Shopify CDN for each breakpoint.
     * @returns {Array}
     */
    sources() {
      const mobileBreakpoints = [
        'tiny',
        'small',
        'medium',
        'large',
      ]

      const sources = []

      Object.keys(breakpoints).forEach((key, index) => {
        let imageObject = this.image

        const mediaQuery = breakpoints[key]

        const size = mediaQuery

        if (this.imageDesktop && !mobileBreakpoints.includes(key)) {
          imageObject = this.imageDesktop
        }

        if (!imageObject.src) {
          return false
        }

        const sizedSrc = getSizedImageUrl(imageObject.src, `${size}x`)

        sources.push({
          breakpoint: key,
          dataSrc: this.lazyload ? sizedSrc : null,
          media: index === (Object.keys(breakpoints).length - 1)
            ? ''
            : `(max-width: ${mediaQuery}px)`,
          size,
          src: this.lazyload ? null : sizedSrc,
        })
      })

      return sources
    },
  },

  watch: {

    /**
     * Listen for if the device has been resized to mobile or desktop.
     */
    isMobile() {
      this.setImageProperties()
    },
  },

  mounted () {
    this.setImageProperties()
  },

  methods: {

    /**
     * Set the aspect ratio as a CSS attribute to size the container.
     * @returns {String}
     */
    setImageProperties() {
      this.state = 'loading'
      this.aspectRatio = this.aspectRatio || this.image.aspectRatio

      if (this.imageDesktop && !this.isMobile) {
        this.aspectRatio = this.imageDesktop.aspectRatio
      }

      if (!this.aspectRatio) {
        this.setRemoteImageProperties()
        return
      }

      if (this.$refs.spacer) {
        this.$refs.spacer.style.setProperty(
          '--aspect-ratio',
          this.aspectRatio,
        )
      }

      this.$emit('set-aspect-ratio', this.aspectRatio)
      this.state = 'ready'
    },

    /**
     * If an image does not contain an aspectRatio, assume only a src URL has
     * been passed in to the component. Create a new image and fetch data.
     * - If an aspect ratio is known before hand (i.e. Product Card), preference
     * is to modify the `this.image` with the aspect ratio before the image
     * is passed into this component.
     */
    async setRemoteImageProperties() {
      if (!this.image.src) {
        return
      }

      try {
        const newImage = await new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = () => reject()
          img.src = this.image.src
        })

        this.aspectRatio = newImage.naturalWidth / newImage.naturalHeight

        this.setImageProperties()

      } catch (error) {
        mint.Log('error', `Error loading image: ${this.image.url}`, error)
      }
    },

    /**
     * Returns a fallback src URL.
     * - Can be used for both lazyload and non-lazyload.
     * @param {Boolean} dataAttr - src is being used for data attribute.
     * @returns {Null|String}
     */
    getFallbackImageSrc(dataAttr) {
      let image = this.image

      if (this.imageDesktop && !this.isMobile) {
        image = this.imageDesktop
      }

      if (
        (!image.src && !image.url) ||
        (this.lazyload && !dataAttr)
      ) {
        return null
      }

      return getSizedImageUrl(image.src || image.url, '100x')
    },
  },
}
</script>
